import React, { useCallback, useState } from "react";
import Cropper from "react-easy-crop";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { ZoomIn, RotateCcw, RotateCw, Move } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { FindoSpinner } from "@/components/FindoSpinner";

const rad = (deg) => (deg * Math.PI) / 180;

function loadImg(src) {
  return new Promise((res, rej) => {
    const i = new Image();
    i.onload = () => res(i);
    i.onerror = rej;
    i.src = src;
  });
}

// Full image (no crop), honouring rotation, scaled down to maxDim.
async function getFullImage(src, rotation = 0, maxDim = 1400, quality = 0.85) {
  const image = await loadImg(src);
  const { width: bw, height: bh } = rotatedSize(image.width, image.height, rotation);
  let outW = bw, outH = bh;
  if (Math.max(outW, outH) > maxDim) {
    const s = maxDim / Math.max(outW, outH);
    outW = Math.round(bw * s); outH = Math.round(bh * s);
  }
  const canvas = document.createElement("canvas");
  canvas.width = outW; canvas.height = outH;
  const ctx = canvas.getContext("2d");
  ctx.translate(outW / 2, outH / 2);
  ctx.rotate(rad(rotation));
  ctx.scale(outW / bw, outH / bh);
  ctx.drawImage(image, -image.width / 2, -image.height / 2);
  return canvas.toDataURL("image/jpeg", quality);
}

function rotatedSize(w, h, rotation) {
  const r = rad(rotation);
  return {
    width: Math.abs(Math.cos(r) * w) + Math.abs(Math.sin(r) * h),
    height: Math.abs(Math.sin(r) * w) + Math.abs(Math.cos(r) * h),
  };
}

async function getCropped(src, crop, rotation = 0, maxDim = 1400, quality = 0.8) {
  const image = await new Promise((res, rej) => {
    const i = new Image();
    i.onload = () => res(i);
    i.onerror = rej;
    i.src = src;
  });
  const { width: bw, height: bh } = rotatedSize(image.width, image.height, rotation);
  const canvas = document.createElement("canvas");
  canvas.width = bw;
  canvas.height = bh;
  const ctx = canvas.getContext("2d");
  ctx.translate(bw / 2, bh / 2);
  ctx.rotate(rad(rotation));
  ctx.drawImage(image, -image.width / 2, -image.height / 2);
  const data = ctx.getImageData(crop.x, crop.y, crop.width, crop.height);

  let outW = crop.width, outH = crop.height;
  if (Math.max(outW, outH) > maxDim) {
    const s = maxDim / Math.max(outW, outH);
    outW = Math.round(outW * s); outH = Math.round(outH * s);
  }
  const tmp = document.createElement("canvas");
  tmp.width = crop.width; tmp.height = crop.height;
  tmp.getContext("2d").putImageData(data, 0, 0);
  const out = document.createElement("canvas");
  out.width = outW; out.height = outH;
  out.getContext("2d").drawImage(tmp, 0, 0, outW, outH);
  return out.toDataURL("image/jpeg", quality);
}

export default function PhotoEditor({ src, open, onClose, onSave, aspect = 4 / 3 }) {
  const { t } = useLang();
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [areaPixels, setAreaPixels] = useState(null);
  const [busy, setBusy] = useState(false);

  const onCropComplete = useCallback((_a, pixels) => setAreaPixels(pixels), []);

  async function save() {
    if (!areaPixels) return;
    setBusy(true);
    try {
      const b64 = await getCropped(src, areaPixels, rotation);
      await onSave(b64);
      onClose();
    } finally { setBusy(false); }
  }

  async function saveFull() {
    setBusy(true);
    try {
      const b64 = await getFullImage(src, rotation);
      await onSave(b64);
      onClose();
    } finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="photo-editor">
        <DialogHeader>
          <DialogTitle className="font-display">{t("hc.adjustThePhoto")}</DialogTitle>
        </DialogHeader>
        <div className="group relative w-full h-80 sm:h-[28rem] bg-slate-900 rounded-xl overflow-hidden">
          {src && (
            <Cropper
              image={src} crop={crop} zoom={zoom} rotation={rotation} aspect={aspect}
              onCropChange={setCrop} onZoomChange={setZoom} onRotationChange={setRotation}
              onCropComplete={onCropComplete} objectFit="contain" showGrid={false}
              style={{ cropAreaStyle: { border: "none", boxShadow: "none" } }}
            />
          )}
          {src && (
            <div
              className="pointer-events-none absolute inset-x-0 bottom-3 flex justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300"
              data-testid="photo-editor-hint">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-black/55 backdrop-blur-sm text-white text-xs font-medium px-3 py-1.5">
                <Move size={13} /> {t("hc.dragToReposition")}
              </span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-3 px-1">
          <ZoomIn size={18} className="text-slate-400 shrink-0" />
          <Slider value={[zoom]} min={1} max={3} step={0.05} onValueChange={(v) => setZoom(v[0])} data-testid="photo-zoom" className="flex-1" />
          <Button type="button" variant="outline" size="icon" onClick={() => setRotation((r) => (r - 90 + 360) % 360)} className="rounded-xl shrink-0" data-testid="photo-rotate-left"><RotateCcw size={16} /></Button>
          <Button type="button" variant="outline" size="icon" onClick={() => setRotation((r) => (r + 90) % 360)} className="rounded-xl shrink-0" data-testid="photo-rotate-right"><RotateCw size={16} /></Button>
        </div>
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose} className="rounded-xl" data-testid="photo-editor-cancel">{t("hc.cancel")}</Button>
          <Button type="button" variant="outline" onClick={saveFull} disabled={busy} className="rounded-xl font-semibold" data-testid="photo-editor-full">
            {busy ? <FindoSpinner size={16} /> : t("hc.fullPhoto")}
          </Button>
          <Button type="button" onClick={save} disabled={busy} className="rounded-xl font-semibold" data-testid="photo-editor-save">
            {busy ? <FindoSpinner size={16} /> : t("hc.confirmCrop")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
